import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CompanyOnboardingService } from '../services/company-onboarding.service';
import { ToastrService } from 'ngx-toastr';
import { CompanyProfile } from '@edtech-ui/core/models/onBord-company/company-profile';
import { INDIAN_LOCATIONS } from '../../core/constants/recuritment-constants/indian-locations.constants';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { EducatorValidators } from '../../core/utils/recruitment-utils/educator-validators';

@Component({
  selector: 'app-company-status-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './company-status-dashboard.component.html',
  styleUrl: './company-status-dashboard.component.scss'
})
export class CompanyStatusDashboardComponent implements OnInit {
  status: 'pending' | 'approved' | 'rejected' | 'clarification' = 'pending';
  isEditing: boolean = false;
  isLoading: boolean = false;
  hasLoadedProfile: boolean = false;
  showSuccessMessage: boolean = false;
  successMessageText: string = '';
  private lastWarningTime = 0;

  rejectionReason: string = 'Company website could not be verified. Please update the website and provide valid company details.';
  clarificationRequest: string = 'Please provide a valid HR contact name and official company email matching your domain.';

  // Main company details matching CompanyProfile model
  companyDetails: CompanyProfile = {
    company_id: '',
    company_name: '',
    company_url: '',
    industry: '',
    linkedin_profile: '',
    about_company: '',
    office_locations: [],
    logo_url: null,
    status: 'pending',
    rejection_reason: null,
    submitted_at: '',
    approved_at: null,
    created_at: '',
    updated_at: '',
    contact: {
      poc_name: '',
      poc_email: '',
      poc_phone: '',
      official_email: '',
      hr_name: '',
      hr_email: '',
      hr_phone: ''
    }
  };

  selectedLogoFile: File | null = null;

  // backup copy to restore on cancel
  backupDetails!: string;

  // Office location autocomplete variables
  newLocationInput: string = '';
  indianLocations = INDIAN_LOCATIONS;
  filteredLocations: string[] = [];
  showLocationsDropdown: boolean = false;

  constructor(
    private router: Router,
    private onboardingService: CompanyOnboardingService,
    private toastr: ToastrService,
    private authService: AuthServiceService
  ) { }

  ngOnInit() {
    this.fetchCompanyProfile();
  }

  fetchCompanyProfile() {
    this.isLoading = true;
    this.onboardingService.getCompanyProfile().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response && response.Success && response.Data) {
          this.companyDetails = response.Data;
          this.hasLoadedProfile = true;
          
          // Map backend status to UI status
          const backendStatus = response.Data.status as string;
          if (backendStatus === 'need_clarification' || backendStatus === 'need clarification' || backendStatus === 'clarification' || backendStatus === 'draft') {
            this.status = 'clarification';
            let reasonText = '';
            const clarifications = (response.Data as any).clarifications;
            if (Array.isArray(clarifications) && clarifications.length > 0) {
              reasonText = clarifications[clarifications.length - 1]?.reason || '';
            }
            this.clarificationRequest = reasonText || (response.Data as any).clarification_reason || response.Data.rejection_reason || 'Please review your details and resubmit.';
          } else if (backendStatus === 'rejected') {
            this.status = 'rejected';
            this.rejectionReason = response.Data.rejection_reason || 'Your application was rejected. Please update your details and resubmit.';
          } else {
            this.status = backendStatus as any;
          }
          this.saveBackup();
        } else {
          this.toastr.error(response.Message || 'Failed to fetch company profile.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('An error occurred while fetching company profile.');
        console.error(err);
      }
    });
  }

  saveBackup() {
    this.backupDetails = JSON.stringify(this.companyDetails);
  }

  restoreBackup() {
    if (this.backupDetails) {
      this.companyDetails = JSON.parse(this.backupDetails);
      this.selectedLogoFile = null;
    }
  }

  setStatus(newStatus: 'pending' | 'approved' | 'rejected' | 'clarification') {
    this.status = newStatus;
    this.isEditing = false;
    this.showSuccessMessage = false;
  }

  enableEditing() {
    this.isEditing = true;
    this.saveBackup();
  }

  cancelEditing() {
    this.isEditing = false;
    this.restoreBackup();
    this.showLocationsDropdown = false;
    this.newLocationInput = '';
  }

  validateForm(): boolean {
    const details = this.companyDetails;

    // 1. About Company (Required, min 10, max 2000)
    const aboutError = EducatorValidators.aboutCompany({ value: details.about_company } as any);
    if (aboutError) {
      if (aboutError['required']) {
        this.toastr.error('About Company is required.');
      } else if (aboutError['minLengthError']) {
        this.toastr.error('About Company must be at least 10 characters.');
      } else if (aboutError['maxLengthError']) {
        this.toastr.error('About Company must not exceed 2000 characters.');
      }
      return false;
    }

    // 2. Office Locations (Required, at least 1)
    if (!details.office_locations || details.office_locations.length === 0) {
      this.toastr.error('At least one office location is required.');
      return false;
    }

    // 3. LinkedIn Profile (Optional, but if present must be valid)
    const linkedinError = EducatorValidators.linkedinCompanyUrl({ value: details.linkedin_profile } as any);
    if (linkedinError) {
      this.toastr.error('Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username or https://linkedin.com/company/companyname).');
      return false;
    }

    // 4. HR Name (Optional, alphabets and spaces only)
    if (details.contact.hr_name && details.contact.hr_name.trim()) {
      const hrNameError = EducatorValidators.educatorName({ value: details.contact.hr_name } as any);
      if (hrNameError) {
        this.toastr.error('HR Name must contain only alphabets and spaces.');
        return false;
      }
    }

    // 5. HR Email (Optional, must be valid email format)
    if (details.contact.hr_email && details.contact.hr_email.trim()) {
      const hrEmailError = EducatorValidators.email({ value: details.contact.hr_email } as any);
      if (hrEmailError) {
        this.toastr.error('Please enter a valid HR Email address.');
        return false;
      }
    }

    // 6. HR Phone Number (Optional, must be valid phone format)
    if (details.contact.hr_phone && details.contact.hr_phone.trim()) {
      const hrPhoneError = EducatorValidators.hrPhone({ value: details.contact.hr_phone } as any);
      if (hrPhoneError) {
        this.toastr.error('Please enter a valid HR Phone Number.');
        return false;
      }
    }

    return true;
  }

  isFormInvalid(): boolean {
    const details = this.companyDetails;

    // 1. About Company (Required, min 10, max 2000)
    const aboutError = EducatorValidators.aboutCompany({ value: details.about_company } as any);
    if (aboutError) return true;

    // 2. Office Locations (Required, at least 1)
    if (!details.office_locations || details.office_locations.length === 0) {
      return true;
    }

    // 3. LinkedIn Profile (Optional, but if present must be valid)
    const linkedinError = EducatorValidators.linkedinCompanyUrl({ value: details.linkedin_profile } as any);
    if (linkedinError) return true;

    // 4. HR Name (Optional, alphabets and spaces only)
    if (this.isHrNameInvalid()) return true;

    // 5. HR Email (Optional, must be valid email format)
    if (this.isHrEmailInvalid()) return true;

    // 6. HR Phone Number (Optional, must be valid phone format)
    if (this.isHrPhoneInvalid()) return true;

    // 7. About Company (Custom validator check)
    if (this.isAboutCompanyInvalid()) return true;

    // 8. LinkedIn Profile (Custom validator check)
    if (this.isLinkedinInvalid()) return true;

    return false;
  }

  updateProfileOnBackend() {
    const formData = new FormData();
    formData.append('company_name', this.companyDetails.company_name || '');
    formData.append('company_url', this.companyDetails.company_url || '');
    formData.append('industry', this.companyDetails.industry || '');
    formData.append('linkedin_profile', this.companyDetails.linkedin_profile || '');
    formData.append('about_company', this.companyDetails.about_company || '');
    
    if (this.companyDetails.office_locations) {
      this.companyDetails.office_locations.forEach((loc) => {
        formData.append('office_locations', loc);
      });
    }
    
    const contact = this.companyDetails.contact;
    formData.append('poc_name', contact.poc_name || '');
    formData.append('poc_email', contact.poc_email || '');
    formData.append('poc_phone', contact.poc_phone || '');
    formData.append('official_email', contact.official_email || '');
    formData.append('hr_name', contact.hr_name || '');
    formData.append('hr_email', contact.hr_email || '');
    formData.append('hr_phone', contact.hr_phone || '');

    if (this.selectedLogoFile) {
      formData.append('logo', this.selectedLogoFile);
    } else if (this.companyDetails.logo_url === null) {
      formData.append('remove_logo', 'true');
    }

    return this.onboardingService.putCompanyProfile(formData, this.companyDetails.company_id);
  }

  saveProfileChanges() {
    if (!this.validateForm()) {
      return;
    }
    this.isLoading = true;
    this.updateProfileOnBackend().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response && response.Success) {
          this.isEditing = false;
          this.selectedLogoFile = null;
          this.showToast('Profile updated successfully.');
          this.fetchCompanyProfile();
        } else {
          this.toastr.error(response.Message || 'Failed to update profile.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('An error occurred while updating the profile.');
        console.error(err);
      }
    });
  }

  resubmitForReview() {
    if (!this.validateForm()) {
      return;
    }
    this.isLoading = true;
    this.updateProfileOnBackend().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response && response.Success) {
          this.isEditing = false;
          this.selectedLogoFile = null;
          this.showToast('Application resubmitted successfully. Your profile is now under review.');
          this.fetchCompanyProfile();
        } else {
          this.toastr.error(response.Message || 'Failed to resubmit application.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error('An error occurred while resubmitting.');
        console.error(err);
      }
    });
  }

  contactSupport() {
    window.location.href = 'mailto:support@mindmentor.com';
  }

  goToDashboard() {
    this.router.navigate(['/recruiter-dashboard/dashboard']);
  }

  showToast(message: string) {
    this.successMessageText = message;
    this.showSuccessMessage = true;
    setTimeout(() => {
      this.showSuccessMessage = false;
    }, 5000);
  }

  // Logo uploader methods
  onLogoSelect(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Only PNG, JPG and SVG files are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.toastr.error('File size should be less than 2MB');
      return;
    }
    this.selectedLogoFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.companyDetails.logo_url = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeLogo(event: Event): void {
    event.stopPropagation();
    this.companyDetails.logo_url = null;
    this.selectedLogoFile = null;
  }

  // Location suggestions logic
  onLocationInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.trim().length >= 1) {
      const query = value.toLowerCase();
      this.filteredLocations = this.indianLocations.filter(loc =>
        loc.toLowerCase().includes(query)
      );
      this.showLocationsDropdown = true;
    } else {
      this.filteredLocations = [];
      this.showLocationsDropdown = false;
    }
  }

  onLocationFocus(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.trim().length >= 1) {
      const query = value.toLowerCase();
      this.filteredLocations = this.indianLocations.filter(loc =>
        loc.toLowerCase().includes(query)
      );
      this.showLocationsDropdown = true;
    } else {
      this.filteredLocations = [];
      this.showLocationsDropdown = false;
    }
  }

  onLocationBlur(): void {
    // delay slightly to allow clicking recommendation item
    setTimeout(() => {
      this.showLocationsDropdown = false;
    }, 200);
  }

  selectLocation(loc: string): void {
    this.newLocationInput = loc;
    this.showLocationsDropdown = false;
    this.filteredLocations = [];
  }

  addLocation(): void {
    const value = this.newLocationInput.trim();
    if (!value) return;

    const matchedLocation = this.indianLocations.find(loc => loc.toLowerCase() === value.toLowerCase());
    if (!matchedLocation) {
      this.toastr.warning('Please select a valid location from the suggestions.');
      return;
    }

    if (!this.companyDetails.office_locations) {
      this.companyDetails.office_locations = [];
    }

    if (!this.companyDetails.office_locations.includes(matchedLocation)) {
      this.companyDetails.office_locations.push(matchedLocation);
    }
    this.newLocationInput = '';
  }

  removeLocation(index: number): void {
    if (this.companyDetails.office_locations) {
      this.companyDetails.office_locations.splice(index, 1);
    }
  }

  isHrNameInvalid(): boolean {
    const details = this.companyDetails;
    if (details.contact && details.contact.hr_name && details.contact.hr_name.trim()) {
      const hrNameError = EducatorValidators.educatorName({ value: details.contact.hr_name } as any);
      return !!hrNameError;
    }
    return false;
  }

  isHrEmailInvalid(): boolean {
    const details = this.companyDetails;
    if (details.contact && details.contact.hr_email && details.contact.hr_email.trim()) {
      const hrEmailError = EducatorValidators.email({ value: details.contact.hr_email } as any);
      return !!hrEmailError;
    }
    return false;
  }

  isHrPhoneInvalid(): boolean {
    const details = this.companyDetails;
    if (details.contact && details.contact.hr_phone && details.contact.hr_phone.trim()) {
      const hrPhoneError = EducatorValidators.hrPhone({ value: details.contact.hr_phone } as any);
      return !!hrPhoneError;
    }
    return false;
  }

  onHrNameInput(): void {
    const details = this.companyDetails;
    if (details.contact && details.contact.hr_name && details.contact.hr_name.trim()) {
      const hrNameError = EducatorValidators.educatorName({ value: details.contact.hr_name } as any);
      if (hrNameError) {
        const now = Date.now();
        if (now - this.lastWarningTime > 3000) {
          this.lastWarningTime = now;
          this.toastr.warning('HR Name must contain only alphabets and spaces.', 'Invalid Name Format', {
            timeOut: 3000
          });
        }
      }
    }
  }

  isLinkedinInvalid(): boolean {
    const details = this.companyDetails;
    if (details.linkedin_profile && details.linkedin_profile.trim()) {
      const error = EducatorValidators.linkedinCompanyUrl({ value: details.linkedin_profile } as any);
      return !!error;
    }
    return false;
  }

  isAboutCompanyInvalid(): boolean {
    const details = this.companyDetails;
    if (this.isEditing) {
      const error = EducatorValidators.aboutCompany({ value: details.about_company } as any);
      return !!error;
    }
    return false;
  }

  logout(): void {
    this.isLoading = true;
    this.authService.logout(this.router).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.Success) {
          this.toastr.success('Logged out successfully.');
        } else {
          this.toastr.error(response.Message || 'Logout failed');
        }
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error('An error occurred during logout');
      }
    });
  }
}

